import { useState, useEffect, useRef } from 'react';
import { Container, Card, Row, Col } from 'react-bootstrap';
import './SharedClient.css';
import { DataMap } from '@grapecity/wijmo.grid';
import { FreeProposalLevels } from 'constants/constants';
import { getFileNameWithoutExt } from 'utils/fileUtils';

const getCircledNumber = (n) =>
  n >= 1 && n <= 20 ? String.fromCharCode(0x2460 + (n - 1)) : String(n);

export const SharedClient = (props) => {
  const [error, setError] = useState('');
  const [freeText, setFreeText] = useState('');
  const [expired, setExpired] = useState('');
  const [meetingHistory, setMeetingHistory] = useState([]);
  const [sharedId, setSharedId] = useState(null);
  const [clientName, setClientName] = useState('');
  const originalTitleRef = useRef(document.title);
  const freeProposalLevelsMap = new DataMap(
    [...FreeProposalLevels.filter((x) => x.key !== -1), { key: -1, value: '' }],
    'key',
    'value'
  );

  // ページ表示時のみタイトルを変更し、離脱時に元へ戻す
  useEffect(() => {
    // 初期表示（名前取得前）の仮タイトル
    document.title = '打ち合わせ履歴（共有）';
    return () => {
      document.title = originalTitleRef.current;
    };
  }, []);

  // クライアント名やエラー取得後にタイトルを更新
  useEffect(() => {
    if (error) {
      document.title = 'ページの有効期限が切れています';
      return;
    }
    if (clientName) {
      document.title = `${clientName}様 打ち合わせ履歴`;
    }
  }, [clientName, error]);

  useEffect(() => {
    // URLパスからGUIDを取得
    const path = window.location.pathname;
    const paths = path.split('/');
    const guid = paths[paths.length - 1];
    setSharedId(guid);

    const baseUrl = process.env.REACT_APP_REST_URL ?? '';
    fetch(`${baseUrl}/api/shared/${guid}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json;' },
      cache: 'no-cache',
      mode: 'cors',
    })
      .then((res) => {
        if (!res.ok) {
          // 404のレスポンスボディからカスタムメッセージを取得
          return res.json().then((errData) => {
            const defaultMsg = '前回打ち合わせプランの共有期間は終了しました。変更等のご連絡お待ちしております';
            setError(errData?.expired_message || defaultMsg);
            return undefined;
          }).catch(() => {
            setError('前回打ち合わせプランの共有期間は終了しました。変更等のご連絡お待ちしております');
            return undefined;
          });
        }
        return res.json();
      })
      .then((data) => {
        if (data?.error) {
          setError(data.error);
        }
        setClientName(data?.client_name ?? '');
        setFreeText(data?.free_text ?? '');
        let histories = data?.meeting_history ?? [];
        setMeetingHistory(histories);
        setExpired(data?.expired ?? '');
      });
  }, []);

  // PDFを表示
  const handleOnClickPdfFile = (fileNo) => {
    const baseUrl = process.env.REACT_APP_REST_URL ?? '';
    window.open(`${baseUrl}/api/shared/pdf/${sharedId}/${fileNo}`, '_blank');
  };

  return error ? (
    <div style={{ whiteSpace: 'pre-wrap', marginLeft: '14px' }}>{error}</div>
  ) : (
    <Container fluid className="glp-shared-container">
      <Row className="d-flex justify-content-center pt-4">
        <Col xs="12" sm="10" md="8" lg="6">
          <Card className="glp-card">
            <Card.Body className="p-3 p-sm-4">
              {error ? (
                <div className="glp-error small">{error}</div>
              ) : (
                <>
                  <h5 className="glp-title">{clientName}様　打ち合わせ履歴</h5>
                  <ul className="glp-timeline">
                    {meetingHistory.map((item, idx) => (
                      <li key={idx} className="glp-timeline-item">
                        {/* 左列: 日付 + FREE_FLG（縦積み） */}
                        <div className="glp-left">
                          <div className="glp-date-badge">{item.MEETING_DATE}</div>
                          {(() => {
                            const freeLabel = freeProposalLevelsMap.getDisplayValue(item.FREE_FLG);
                            return freeLabel ? (
                              <div className="glp-free-badge">{freeLabel}</div>
                            ) : null;
                          })()}
                        </div>

                        {/* 右列: 打ち合わせ内容 + ファイルリンク */}
                        <div className="glp-right glp-note">
                          <div className="glp-note-text font-weight-bold" style={{ whiteSpace: 'pre-wrap' }}>
                            {item.MEETING_CONTENT}
                          </div>

                          {item?.NOTE && (
                            <div className="glp-notice-body" style={{ whiteSpace: 'pre-wrap' }}>
                              {item.NOTE}
                            </div>
                          )}

                          <div className="glp-note-links">
                            {(() => {
                              const movieLinks = [1, 2, 3, 4, 5]
                                .filter((i) => item[`MOVIE${i}_URL`])
                                .map((i) => ({
                                  key: `movie-${i}`,
                                  type: 'movie',
                                  href: item[`MOVIE${i}_URL`],
                                  title: item[`MOVIE${i}_TITLE`],
                                }));

                              const tempLinks = [1, 2, 3, 4, 5, 6]
                                .filter((i) => item[`TEMP_${i}_FILE_NAME`])
                                .map((i) => ({
                                  key: `temp-${i}`,
                                  type: 'temp',
                                  fileNo: item[`TEMP_${i}_FILE_NO`],
                                  title: getFileNameWithoutExt(item[`TEMP_${i}_FILE_NAME`]),
                                }));

                              const links = [...movieLinks, ...tempLinks];

                              return links.map((link, idx) =>
                                link.type === 'movie' ? (
                                  <a
                                    key={link.key}
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {getCircledNumber(idx + 1)} {link.title}
                                  </a>
                                ) : (
                                  <a
                                    key={link.key}
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleOnClickPdfFile(link.fileNo);
                                    }}
                                  >
                                    {getCircledNumber(idx + 1)} {link.title}
                                  </a>
                                )
                              );
                            })()}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {/* フリーテキスト */}
                  {freeText && <div className="mt-3 glp-shared-intro" style={{ whiteSpace: 'pre-wrap' }}>{freeText}</div>}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};
